import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
} from '@mlm/ui';
import {
  createSupplierRefund,
  createSupplierReturn,
  getReturnableForPo,
  listFundsAccounts,
  listPurchaseOrders,
  listSupplierRefunds,
  listSupplierReturns,
  voidSupplierRefund,
  voidSupplierReturn,
  type ReturnableForPo,
  type SupplierReturnRow,
} from '../../lib/api';
import {
  defaultFundsAccountFor,
  SUPPLIER_RETURN_CREDIT_KIND_LABELS,
  SUPPLIER_RETURN_REASON_LABELS,
  SUPPLIER_RETURN_REASONS,
} from '@mlm/shared';
import { useHasPerm } from '../../lib/auth-store';
import { Money } from './_shared';

/**
 * Returns to supplier (RTS-) — the list panel + entry dialog, rendered by both
 * Payables and the standalone /accounting/supplier-returns page. A return
 * linked to a posted bill credits that bill; one on a received-but-unbilled PO
 * reverses GRNI; an ad-hoc one leaves an open supplier credit.
 */

// ── display helpers (duplicated from payables.tsx on purpose: exporting
//    non-components from a page module breaks Fast Refresh) ─────────────────

function peso(v: string | number | null | undefined): string {
  const n = Number(v ?? 0);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

/** Local-timezone YYYY-MM-DD for `<input type="date">` defaults. */
function todayInput(): string {
  return new Date().toLocaleDateString('en-CA');
}

function errText(error: unknown): string | undefined {
  return (error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
}

const ERROR_COPY: Record<string, string> = {
  bill_link_required:
    'This PO already has a posted bill — link the return to that bill so the credit lands on it.',
  over_return: 'More units than were received on that PO line.',
  credit_exceeds_bill: 'Returns against this bill would exceed the bill total.',
  branch_not_house: 'Returns leave a house branch — a stockist branch cannot be the source.',
  out_of_stock: 'The branch no longer holds that many units.',
  cost_required: 'This product has no cost — enter a unit cost for the return.',
  zero_value_return: 'The return has no value.',
  po_not_received: 'Nothing has been received on that PO yet.',
  return_has_refunds: 'A supplier refund is recorded against this return — void it first.',
  refund_exceeds_credit: "More than this return's remaining open credit.",
  refund_underflow: 'That refund was already reversed.',
  grni_refund_not_supported:
    'This return reversed goods-received-not-invoiced, not a payable — there is nothing for the supplier to refund.',
  return_void: 'This return is void.',
  invalid_amount: 'Enter an amount greater than zero.',
  funds_account_required: 'Pick the account the money landed in.',
  already_void: 'This document is already void.',
  po_supplier_mismatch: 'That PO belongs to a different supplier.',
  po_not_found: 'Purchase order not found.',
  supplier_not_found: 'Supplier not found.',
  accounting_disabled: 'Accounting is switched off for this tenant.',
  validation_error:
    'A field has an invalid value — check quantities and amounts (up to 4 decimals).',
};

function friendly(error: unknown, fallback: string): string {
  const code = errText(error);
  if (!code) return fallback;
  // `over_return:<lineId>` / `out_of_stock:<id>` / `cost_required:<id>` carry an id suffix.
  const bare = code.split(':')[0]!;
  return ERROR_COPY[code] ?? ERROR_COPY[bare] ?? code;
}

const SELECT_CLS = 'h-9 rounded-md border border-input bg-background px-3 text-sm';

/** The server's moneyStringSchema takes no thousands separators. */
function sanitizeMoney(v: string): string {
  return v.replace(/,/g, '').trim();
}

const PAYMENT_METHODS: Array<[string, string]> = [
  ['cash', 'Cash'],
  ['bank_transfer', 'Bank transfer'],
  ['gcash', 'GCash'],
  ['check', 'Check'],
  ['other', 'Other'],
];

function CreditKindBadge({ kind }: { kind: SupplierReturnRow['creditKind'] }) {
  const cls =
    kind === 'bill_credit'
      ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200'
      : kind === 'grni_relief'
        ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
        : 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200';
  return <Badge className={cls}>{SUPPLIER_RETURN_CREDIT_KIND_LABELS[kind] ?? kind}</Badge>;
}

/**
 * Goods sent back to a supplier. Void = stock back + credit un-applied.
 * Printing rides `view.accounting` (the print route's own gate), so the Print
 * action shows for every row — read-only staff and void rows included.
 */
export function SupplierReturnsPanel({
  suppliers,
  onChanged,
}: {
  suppliers: Array<{ id: string; name: string; code: string }>;
  onChanged: () => void;
}) {
  const canWrite = useHasPerm('supplier_return.write');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [refunding, setRefunding] = useState<SupplierReturnRow | null>(null);
  const [viewingRefunds, setViewingRefunds] = useState<SupplierReturnRow | null>(null);
  const returnsQ = useQuery({
    queryKey: ['supplier-returns', supplierFilter],
    queryFn: () => listSupplierReturns(supplierFilter ? { supplierId: supplierFilter } : {}),
  });
  const voidReturn = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => voidSupplierReturn(id, reason),
    meta: { successMessage: 'Return voided — stock and credit restored.' },
    onSuccess: onChanged,
    onError: (err) => toast.error(friendly(err, 'Could not void the return.')),
  });
  function askVoid(r: SupplierReturnRow) {
    const reason = window.prompt(`Void return ${r.docNo}? Enter a reason:`)?.trim();
    if (!reason) return;
    if (reason.length < 3) {
      toast.error('Reason must be at least 3 characters.');
      return;
    }
    voidReturn.mutate({ id: r.id, reason });
  }
  const rows = returnsQ.data ?? [];
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>
          Returns to supplier{' '}
          <span className="text-sm font-normal text-muted-foreground">({rows.length})</span>
        </CardTitle>
        <div className="flex items-center gap-2">
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            className={`${SELECT_CLS} min-w-[12rem]`}
          >
            <option value="">All suppliers</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
          {canWrite ? (
            <Button size="sm" onClick={() => setCreating(true)}>
              + New return
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {returnsQ.isLoading ? (
          <p className="p-4 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            No returns yet. Goods sent back to a supplier are recorded here; a return against a
            posted bill reduces that bill's balance.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">RTS no.</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Supplier</th>
                  <th className="px-4 py-2">PO / Bill</th>
                  <th className="px-4 py-2">Kind</th>
                  <th className="px-4 py-2">Reason</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2 text-right">Applied</th>
                  <th className="px-4 py-2 text-right">Open credit</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b last:border-0 hover:bg-muted/30 ${
                      r.status === 'void' ? 'opacity-60' : ''
                    }`}
                  >
                    <td className="px-4 py-2 font-mono text-xs">{r.docNo}</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {shortDate(r.returnedAt)}
                    </td>
                    <td className="px-4 py-2">{r.supplierName}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {r.poNumber ?? '—'}
                      {r.invoiceNo ? ` · ${r.invoiceNo}` : ''}
                    </td>
                    <td className="px-4 py-2">
                      <CreditKindBadge kind={r.creditKind} />
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {SUPPLIER_RETURN_REASON_LABELS[
                        r.reason as keyof typeof SUPPLIER_RETURN_REASON_LABELS
                      ] ?? r.reason}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Money>{peso(r.total)}</Money>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Money>{peso(r.amountApplied)}</Money>
                    </td>
                    <td className="px-4 py-2 text-right">
                      {r.status === 'void' || Number(r.openCredit) === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        <Money>{peso(r.openCredit)}</Money>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Badge variant={r.status === 'void' ? 'outline' : 'secondary'}>
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            window.open(
                              `/accounting/supplier-returns/${r.id}/print`,
                              '_blank',
                              'noopener',
                            )
                          }
                        >
                          Print
                        </Button>
                        {/* A GRNI reversal relieved goods-received-not-invoiced,
                            not a payable — there is no cash to ask back. */}
                        {canWrite &&
                        r.status === 'posted' &&
                        r.creditKind !== 'grni_relief' &&
                        Number(r.openCredit) > 0 ? (
                          <Button size="sm" variant="ghost" onClick={() => setRefunding(r)}>
                            Refund
                          </Button>
                        ) : null}
                        {/* The only way back out of a refund: a return that has
                            one refuses its own void (return_has_refunds) until
                            each SREF- is voided here. */}
                        {Number(r.amountRefunded) > 0 ? (
                          <Button size="sm" variant="ghost" onClick={() => setViewingRefunds(r)}>
                            Refunds
                          </Button>
                        ) : null}
                        {canWrite && r.status === 'posted' ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            disabled={voidReturn.isPending}
                            onClick={() => askVoid(r)}
                          >
                            Void
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {creating ? (
        <NewReturnDialog
          suppliers={suppliers}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            onChanged();
          }}
        />
      ) : null}
      {viewingRefunds ? (
        <RefundsDialog
          rts={viewingRefunds}
          canWrite={canWrite}
          onClose={() => setViewingRefunds(null)}
          onChanged={() => {
            void returnsQ.refetch();
            onChanged();
          }}
        />
      ) : null}
      {refunding ? (
        <RefundDialog
          rts={refunding}
          onClose={() => setRefunding(null)}
          onSaved={() => {
            setRefunding(null);
            void returnsQ.refetch();
            onChanged();
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * Cash the supplier sent back against one return (SREF-). Cloned from the bill
 * PayDialog with the direction flipped: the amount is capped at the return's
 * OPEN credit (total − applied − refunded, server-computed) and the funds
 * picker is branchless — `supplier_refunds` carries no branch, so a
 * branch-bound till would be refused server-side.
 */
function RefundDialog({
  rts,
  onClose,
  onSaved,
}: {
  rts: SupplierReturnRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const openCredit = Number(rts.openCredit);
  const [amount, setAmount] = useState(openCredit > 0 ? openCredit.toFixed(2) : '');
  const [method, setMethod] = useState('cash');
  const [reference, setReference] = useState('');
  const [paidAt, setPaidAt] = useState(todayInput());
  const [memo, setMemo] = useState('');
  // One idempotency key per open dialog — a double-click can't refund twice.
  const [clientRequestId] = useState(() => crypto.randomUUID());

  const fundsQ = useQuery({
    queryKey: ['funds-accounts', 'active'],
    queryFn: () => listFundsAccounts(true),
  });
  const fundsAccounts = (fundsQ.data?.rows ?? []).filter((a) => a.branchId === null);
  const fundsRequired = fundsQ.data?.required ?? false;
  const [fundsTouched, setFundsTouched] = useState(false);
  const [fundsAccountId, setFundsAccountId] = useState('');
  const suggested = defaultFundsAccountFor(fundsAccounts, { method, branchId: null }) ?? '';
  const effectiveFundsAccountId = fundsTouched ? fundsAccountId : suggested;

  const over = Number(sanitizeMoney(amount)) > openCredit;

  const refund = useMutation({
    mutationFn: () =>
      createSupplierRefund(rts.id, {
        amount: sanitizeMoney(amount),
        method,
        reference: reference.trim() || null,
        paidAt: paidAt ? new Date(`${paidAt}T00:00:00`).toISOString() : null,
        memo: memo.trim() || null,
        clientRequestId,
        fundsAccountId: effectiveFundsAccountId || null,
      }),
    meta: { successMessage: 'Refund recorded.' },
    onSuccess: onSaved,
    onError: (err) => toast.error(friendly(err, 'Could not record the refund.')),
  });

  const canSubmit =
    !!amount.trim() && !over && !refund.isPending && (!fundsRequired || !!effectiveFundsAccountId);

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Refund on {rts.docNo}</DialogTitle>
          <DialogDescription>
            {rts.supplierName} · open credit{' '}
            <span className="font-medium tabular-nums text-foreground">{peso(rts.openCredit)}</span>
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) refund.mutate();
          }}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Amount</label>
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                className={over ? 'border-destructive' : ''}
                required
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Method</label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className={`${SELECT_CLS} w-full`}
              >
                {PAYMENT_METHODS.map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            {(fundsAccounts.length > 0 || fundsRequired) && (
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Received into{fundsRequired ? '' : ' (optional)'}
                </label>
                <select
                  className={SELECT_CLS}
                  value={effectiveFundsAccountId}
                  onChange={(e) => {
                    setFundsTouched(true);
                    setFundsAccountId(e.target.value);
                  }}
                >
                  <option value="">— none —</option>
                  {fundsAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Reference (optional)</label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Check no., transfer ref…"
                maxLength={120}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Date</label>
              <Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Memo</label>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="min-h-14 w-full rounded-md border border-input bg-background p-2 text-sm"
              maxLength={500}
            />
          </div>

          {over ? (
            <p className="text-xs text-destructive">
              More than the return&apos;s remaining open credit.
            </p>
          ) : null}
          {refund.error != null && (
            <p className="text-sm text-destructive">
              {friendly(refund.error, 'Failed to record the refund.')}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {refund.isPending ? 'Recording…' : 'Record refund'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The SREF- documents recorded against one return, with a Void per posted one.
 * Voiding a refund reverses its register row and restores the return's open
 * credit, which is what lets a refunded return be voided at all.
 */
function RefundsDialog({
  rts,
  canWrite,
  onClose,
  onChanged,
}: {
  rts: SupplierReturnRow;
  canWrite: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const refundsQ = useQuery({
    queryKey: ['supplier-refunds', rts.id],
    queryFn: () => listSupplierRefunds(rts.id),
  });
  const voidRefund = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => voidSupplierRefund(id, reason),
    meta: { successMessage: 'Refund voided — the credit is open again.' },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['supplier-refunds', rts.id] });
      onChanged();
    },
    onError: (err) => toast.error(friendly(err, 'Could not void the refund.')),
  });
  function askVoid(docNo: string, id: string) {
    const reason = window.prompt(`Void refund ${docNo}? Enter a reason:`)?.trim();
    if (!reason) return;
    if (reason.length < 3) {
      toast.error('Reason must be at least 3 characters.');
      return;
    }
    voidRefund.mutate({ id, reason });
  }
  const rows = refundsQ.data ?? [];
  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Refunds on {rts.docNo}</DialogTitle>
          <DialogDescription>
            {rts.supplierName} · refunded{' '}
            <span className="font-medium tabular-nums text-foreground">
              {peso(rts.amountRefunded)}
            </span>{' '}
            of {peso(rts.total)}
          </DialogDescription>
        </DialogHeader>
        {refundsQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No refunds recorded on this return.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">SREF no.</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Method</th>
                  <th className="py-2 pr-3">Received into</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr
                    key={f.id}
                    className={`border-b last:border-0 ${f.status === 'void' ? 'opacity-60' : ''}`}
                  >
                    <td className="py-2 pr-3 font-mono text-xs">{f.docNo}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">
                      {shortDate(f.paidAt)}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {PAYMENT_METHODS.find(([code]) => code === f.method)?.[1] ?? f.method}
                      {f.reference ? (
                        <span className="ml-1 text-muted-foreground">· {f.reference}</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-xs">{f.fundsAccountName ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">
                      <Money>{peso(f.amount)}</Money>
                    </td>
                    <td className="py-2 pr-3">
                      <Badge variant={f.status === 'void' ? 'outline' : 'secondary'}>
                        {f.status}
                      </Badge>
                      {f.status === 'void' && f.voidReason ? (
                        <div className="mt-0.5 text-[11px] text-muted-foreground">
                          {f.voidReason}
                        </div>
                      ) : null}
                    </td>
                    <td className="py-2 text-right">
                      {canWrite && f.status === 'posted' ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          disabled={voidRefund.isPending}
                          onClick={() => askVoid(f.docNo, f.id)}
                        >
                          Void
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function NewReturnDialog({
  suppliers,
  onClose,
  onSaved,
  initialSupplierId,
  initialPoId,
}: {
  suppliers: Array<{ id: string; name: string; code: string }>;
  onClose: () => void;
  onSaved: () => void;
  /** Deep-link seeding (PO detail's "Return to supplier" button). */
  initialSupplierId?: string;
  initialPoId?: string;
}) {
  const qc = useQueryClient();
  const [supplierId, setSupplierId] = useState(initialSupplierId ?? '');
  const [poId, setPoId] = useState(initialPoId ?? '');
  const [qty, setQty] = useState<Record<string, string>>({});
  const [billId, setBillId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [reason, setReason] = useState<string>('damaged');
  const [supplierRef, setSupplierRef] = useState('');
  const [memo, setMemo] = useState('');
  const [returnedAt, setReturnedAt] = useState(todayInput());
  // One idempotency key per open dialog — a double-click can't return twice.
  const [clientRequestId] = useState(() => crypto.randomUUID());

  const posQ = useQuery({
    queryKey: ['purchase-orders', 'received', supplierId],
    queryFn: () => listPurchaseOrders({ supplierId }),
    enabled: !!supplierId,
  });
  const pos = (posQ.data ?? []).filter(
    (p) => p.status === 'incomplete' || p.status === 'completed',
  );
  const returnableQ = useQuery({
    queryKey: ['supplier-returnable', poId],
    queryFn: () => getReturnableForPo(poId),
    enabled: !!poId,
  });
  const returnable: ReturnableForPo | undefined = returnableQ.data;
  const lines = (returnable?.lines ?? []).filter((l) => l.returnable > 0);
  // Defaults once the PO loads: sole bill / first house branch.
  const effectiveBillId =
    billId || (returnable && returnable.bills.length === 1 ? returnable.bills[0]!.id : '');
  const effectiveBranchId = branchId || returnable?.branches[0]?.id || '';

  const qtyOf = (poLineId: string) => {
    const n = Number(qty[poLineId] ?? '');
    return Number.isInteger(n) && n > 0 ? n : 0;
  };
  const overSome = lines.some((l) => qtyOf(l.poLineId) > l.returnable);
  const total = lines.reduce((s, l) => s + qtyOf(l.poLineId) * Number(l.unitCost), 0);
  const anyQty = lines.some((l) => qtyOf(l.poLineId) > 0);
  const billMissing = !!returnable?.billLinkRequired && !effectiveBillId;
  const linkedBill = returnable?.bills.find((b) => b.id === effectiveBillId);

  const create = useMutation({
    mutationFn: () =>
      createSupplierReturn({
        supplierId,
        branchId: effectiveBranchId,
        poId,
        supplierInvoiceId: effectiveBillId || null,
        // Local calendar day → instant, so "Aug 27" is booked on Aug 27.
        returnedAt: new Date(`${returnedAt}T00:00:00`).toISOString(),
        reason,
        supplierRef: supplierRef.trim() || null,
        memo: memo.trim() || null,
        lines: lines
          .filter((l) => qtyOf(l.poLineId) > 0)
          .map((l) => ({
            poLineId: l.poLineId,
            productId: l.productId,
            variantId: l.variantId,
            qty: qtyOf(l.poLineId),
          })),
        clientRequestId,
      }),
    onSuccess: (row) => {
      toast.success(`Return ${row.docNo} recorded.`);
      // The PO detail/list show qtyReturned — the shared refresh sets don't
      // cover the PO keys.
      if (poId) {
        void qc.invalidateQueries({ queryKey: ['purchase-order', poId] });
        void qc.invalidateQueries({ queryKey: ['purchase-orders'] });
      }
      onSaved();
    },
  });

  const canSubmit =
    !!supplierId &&
    !!poId &&
    !!effectiveBranchId &&
    anyQty &&
    !overSome &&
    !billMissing &&
    !create.isPending;

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Return to supplier</DialogTitle>
          <DialogDescription>
            Pick the PO the goods came in on. Units leave the branch's stock at the PO cost; a
            return against a posted bill credits that bill.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) create.mutate();
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Supplier</label>
              <select
                className={`${SELECT_CLS} w-full`}
                value={supplierId}
                onChange={(e) => {
                  setSupplierId(e.target.value);
                  setPoId('');
                  setQty({});
                  setBillId('');
                  setBranchId('');
                }}
              >
                <option value="">— choose —</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Purchase order (received)</label>
              <select
                className={`${SELECT_CLS} w-full`}
                value={poId}
                disabled={!supplierId}
                onChange={(e) => {
                  setPoId(e.target.value);
                  setQty({});
                  setBillId('');
                  setBranchId('');
                }}
              >
                <option value="">
                  {!supplierId
                    ? '— pick a supplier first —'
                    : pos.length
                      ? '— choose —'
                      : 'No received POs'}
                </option>
                {pos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.poNumber} · {p.status} · {peso(p.total)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {poId && returnableQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading PO lines…</p>
          ) : null}
          {returnable ? (
            lines.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Every received unit on this PO has already been returned.
              </p>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">Received</th>
                      <th className="px-3 py-2 text-right">Returned</th>
                      <th className="px-3 py-2 text-right">Unit cost</th>
                      <th className="px-3 py-2 text-right">Return qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => {
                      const q = qtyOf(l.poLineId);
                      const over = q > l.returnable;
                      return (
                        <tr key={l.poLineId} className="border-b last:border-0">
                          <td className="px-3 py-2">
                            {l.productName}
                            {l.variantName ? ` — ${l.variantName}` : ''}
                            <span className="ml-1 font-mono text-xs text-muted-foreground">
                              {l.sku}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">{l.qtyFulfilled}</td>
                          <td className="px-3 py-2 text-right">{l.qtyReturned}</td>
                          <td className="px-3 py-2 text-right">
                            <Money>{peso(l.unitCost)}</Money>
                          </td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              type="number"
                              min={0}
                              max={l.returnable}
                              step={1}
                              className={`ml-auto h-8 w-24 text-right ${over ? 'border-destructive' : ''}`}
                              value={qty[l.poLineId] ?? ''}
                              placeholder={`≤ ${l.returnable}`}
                              onChange={(e) =>
                                setQty((prev) => ({ ...prev, [l.poLineId]: e.target.value }))
                              }
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : null}

          {returnable ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">
                  Credit against bill{returnable.billLinkRequired ? '' : ' (none posted yet)'}
                </label>
                <select
                  className={`${SELECT_CLS} w-full`}
                  value={effectiveBillId}
                  disabled={returnable.bills.length === 0}
                  onChange={(e) => setBillId(e.target.value)}
                >
                  <option value="">
                    {returnable.bills.length === 0
                      ? '— not billed: reverses GRNI —'
                      : '— choose the bill —'}
                  </option>
                  {returnable.bills.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.invoiceNo} · {b.status} · balance {peso(b.outstanding)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Stock leaves branch</label>
                <select
                  className={`${SELECT_CLS} w-full`}
                  value={effectiveBranchId}
                  onChange={(e) => setBranchId(e.target.value)}
                >
                  {returnable.branches.length === 0 ? (
                    <option value="">No house branch received this PO</option>
                  ) : null}
                  {returnable.branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Reason</label>
              <select
                className={`${SELECT_CLS} w-full`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                {SUPPLIER_RETURN_REASONS.map((r) => (
                  <option key={r} value={r}>
                    {SUPPLIER_RETURN_REASON_LABELS[r]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Supplier ref (optional)</label>
              <Input
                value={supplierRef}
                onChange={(e) => setSupplierRef(e.target.value)}
                placeholder="RMA / credit memo no."
                maxLength={120}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Date</label>
              <Input
                type="date"
                value={returnedAt}
                onChange={(e) => setReturnedAt(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Memo</label>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              className="min-h-14 w-full rounded-md border border-input bg-background p-2 text-sm"
              maxLength={500}
            />
          </div>

          <div className="rounded-md bg-muted/40 p-3 text-sm">
            <div className="flex items-center justify-between">
              <span>Return value</span>
              <Money>{peso(total)}</Money>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {linkedBill
                ? `Credits bill ${linkedBill.invoiceNo} (balance ${peso(linkedBill.outstanding)}); anything beyond that stays as an open supplier credit.`
                : returnable && !returnable.billLinkRequired
                  ? 'Nothing billed on this PO yet — reverses goods-received-not-invoiced.'
                  : 'Pick a PO to see the effect.'}
            </p>
            {billMissing ? (
              <p className="mt-1 text-xs text-destructive">
                This PO has a posted bill — link the return to it.
              </p>
            ) : null}
            {overSome ? (
              <p className="mt-1 text-xs text-destructive">
                A quantity exceeds what can be returned.
              </p>
            ) : null}
          </div>

          {create.error != null && (
            <p className="text-sm text-destructive">
              {friendly(create.error, 'Failed to record the return.')}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {create.isPending ? 'Recording…' : 'Record return'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
